import React, { useState, useRef, useEffect, useContext, useMemo } from "react";
import PropTypes from "prop-types";
import { useQuery } from "@tanstack/react-query";
import { UserContext } from "../../shared/context/userContext";
import { useTranslation } from "react-i18next";
import { Box, CircularProgress, Typography } from "@mui/material";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import CheckBox from "../../shared/checkBox/CheckBox";
import ChecklistPDF from "../../onboarding/ChecklistPDF";
import EMPButton from "../../shared/components/EMPButton/EMPButton";
import EMPConfirmationBanner from "../../shared/components/EMPConfirmationBanner/EMPConfirmationBanner";
import { IS_FORD_BRAND } from "../../utils/EmpUtil";
import OnboardingDocumentsClient from "../../clients/OnboardingDocumentsClient";
import {
  getEnrollmentTermsFilename,
  getTermsAcceptanceSummary,
} from "../../utils/enrollmentTermsUtil";
import styles from "./TermsAndConditions.module.scss";

const TermsAndConditions = ({
  onAccept = () => {},
  testId = "terms-and-conditions",
  termsType = null,
  acceptanceInfo = null,
  /** When true, T&C substep is already completed — show audit line, disable accept flow, keep PDF/download. */
  termsAccepted = false,
  acceptBlocked = false,
  saveInProgress = false,
  /** When true, show the transient "you have signed" confirmation banner at the top. */
  showConfirmation = false,
  onConfirmationClose = () => {},
}) => {
  const [hasScrolledToBottom, setHasScrolledToBottom] = useState(false);
  const [isCheckboxChecked, setIsCheckboxChecked] = useState(false);
  const scrollContainerRef = useRef(null);
  const { user } = useContext(UserContext);
  const isFordBrand = IS_FORD_BRAND(user?.brand);
  const { t } = useTranslation();

  const {
    data: pdfBlob,
    isLoading: pdfLoading,
    isError: pdfQueryError,
    error: pdfQueryErrorDetail,
  } = useQuery({
    queryKey: ["onboardingTermsPdf", termsType],
    queryFn: () => OnboardingDocumentsClient.getDealerTermsPdf(termsType),
    enabled: Boolean(user) && Boolean(termsType),
    staleTime: 5 * 60 * 1000,
  });

  const pdfObjectUrl = useMemo(() => {
    if (pdfBlob == null) {
      return null;
    }
    return URL.createObjectURL(pdfBlob);
  }, [pdfBlob]);

  useEffect(() => {
    return () => {
      if (pdfObjectUrl) {
        URL.revokeObjectURL(pdfObjectUrl);
      }
    };
  }, [pdfObjectUrl]);

  useEffect(() => {
    if (termsAccepted) {
      setHasScrolledToBottom(true);
      setIsCheckboxChecked(true);
    }
  }, [termsAccepted]);

  const hasUser = Boolean(user);
  const pdfFetching = hasUser && pdfLoading;
  let pdfError = null;
  if (!hasUser) {
    pdfError = new Error("missing_user");
  } else if (pdfQueryError) {
    pdfError = pdfQueryErrorDetail;
  }

  const handleScroll = () => {
    if (scrollContainerRef.current) {
      const { scrollTop, scrollHeight, clientHeight } =
        scrollContainerRef.current;
      // Only enable if there's actually scrollable content
      const hasScrollableContent = scrollHeight > clientHeight;
      // Consider scrolled to bottom if within 10px of the bottom
      const isAtBottom =
        hasScrollableContent && scrollHeight - scrollTop - clientHeight < 10;

      if (isAtBottom && !hasScrolledToBottom) {
        setHasScrolledToBottom(true);
      }
    }
  };

  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener("scroll", handleScroll);
      handleScroll();

      return () => {
        scrollContainer.removeEventListener("scroll", handleScroll);
      };
    }

    return undefined;
  }, [pdfObjectUrl]);

  const handleCheckboxChange = (checked) => {
    if (termsAccepted) {
      return;
    }
    if (hasScrolledToBottom) {
      setIsCheckboxChecked(checked);
    }
  };

  const handleAcceptClick = () => {
    if (termsAccepted) {
      return;
    }
    if (onAccept && isCheckboxChecked && !acceptBlocked && !saveInProgress) {
      onAccept();
    }
  };

  const acceptanceSummaryText = termsAccepted
    ? getTermsAcceptanceSummary(t, acceptanceInfo)
    : null;

  const handleDownload = () => {
    if (!pdfObjectUrl) {
      return;
    }
    const link = document.createElement("a");
    link.setAttribute("href", pdfObjectUrl);
    link.setAttribute("download", getEnrollmentTermsFilename(termsType));
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <Box className={styles.container} data-testid={testId}>
      {showConfirmation && (
        <EMPConfirmationBanner
          variant="success"
          className={styles.confirmationBanner}
          primaryText={t(
            "DealerOnboarding.content.termsAndConditions.acceptedConfirmation",
            {
              defaultValue: "You have signed the Terms & Conditions!",
            }
          )}
          closeAriaLabel={t(
            "DealerOnboarding.content.termsAndConditions.closeConfirmationBanner",
            {
              defaultValue: "Close confirmation banner",
            }
          )}
          onClose={onConfirmationClose}
          testId="terms-confirmation-banner"
        />
      )}
      <Box className={styles.introduction} data-testid="introduction">
        <Typography variant="body1" className={styles.introductionText}>
          {t("DealerOnboarding.content.termsAndConditions.description", {
            currentBrand: isFordBrand ? "Ford" : "Lincoln",
          })}
        </Typography>
      </Box>

      <Box
        ref={scrollContainerRef}
        className={styles.termsBox}
        data-testid="terms-box"
      >
        <Typography
          variant="body1"
          component="div"
          className={styles.termsText}
        >
          {pdfFetching && (
            <Box data-testid="terms-pdf-loading">
              <Typography variant="body2">
                {t("DealerOnboarding.content.termsAndConditions.loadingPdf", {
                  defaultValue: "Loading document…",
                })}
              </Typography>
            </Box>
          )}
          {!pdfFetching && pdfError && (
            <Box data-testid="terms-pdf-error">
              <Typography color="error" variant="body2">
                {t("DealerOnboarding.content.termsAndConditions.pdfLoadError", {
                  defaultValue:
                    "Could not load terms document. Please try again later.",
                })}
              </Typography>
            </Box>
          )}
          {!pdfFetching && pdfObjectUrl && <ChecklistPDF file={pdfObjectUrl} />}
        </Typography>
      </Box>

      <Box
        className={styles.downloadButtonContainer}
        data-testid="download-container"
      >
        <EMPButton
          variant="text"
          disabled={!pdfObjectUrl}
          testId="download-button"
          onClick={handleDownload}
          isFord={isFordBrand}
          leftIcon={<FileDownloadOutlinedIcon fontSize="small" />}
          sx={{ minWidth: "auto", padding: 0 }}
        >
          {t("DealerOnboarding.content.termsAndConditions.download")}
        </EMPButton>
        {acceptanceSummaryText && (
          <Typography
            variant="body2"
            className={styles.acceptanceInfo}
            data-testid="terms-acceptance-summary"
          >
            {acceptanceSummaryText}
          </Typography>
        )}
      </Box>

      {!termsAccepted && (
        <Box
          className={styles.checkboxContainer}
          data-testid="checkbox-container"
        >
          <CheckBox
            isFordBrand={isFordBrand}
            id="terms-and-conditions-checkbox"
            checked={isCheckboxChecked}
            isDisabled={!hasScrolledToBottom}
            handleOnChange={handleCheckboxChange}
            label={t(
              "DealerOnboarding.content.termsAndConditions.checkBoxLabel"
            )}
            ariaLabel="Accept terms and conditions"
          />
        </Box>
      )}

      {!termsAccepted && (
        <Box
          className={styles.acceptButtonContainer}
          data-testid="accept-button-container"
        >
          <EMPButton
            variant="contained"
            disabled={!isCheckboxChecked || acceptBlocked || saveInProgress}
            testId="accept-button"
            onClick={handleAcceptClick}
            isFord={isFordBrand}
          >
            {saveInProgress && (
              <CircularProgress
                size={16}
                color="inherit"
                data-testid="accept-button-spinner"
                sx={{ mr: 1 }}
              />
            )}
            {t(
              "DealerOnboarding.content.termsAndConditions.acceptTermsAndConditions"
            )}
          </EMPButton>
        </Box>
      )}
    </Box>
  );
};

TermsAndConditions.propTypes = {
  onAccept: PropTypes.func,
  testId: PropTypes.string,
  termsType: PropTypes.oneOf(["FORD", "LINCOLN", "DUAL"]),
  acceptanceInfo: PropTypes.shape({
    date: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    cdsid: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  termsAccepted: PropTypes.bool,
  acceptBlocked: PropTypes.bool,
  saveInProgress: PropTypes.bool,
  showConfirmation: PropTypes.bool,
  onConfirmationClose: PropTypes.func,
};

export default TermsAndConditions;
